import { createPortal } from "react-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import dayjs from "dayjs";
import {
	Button,
	Card,
	Checkbox,
	Col,
	DatePicker,
	Form,
	Input,
	InputNumber,
	Modal,
	Popconfirm,
	Row,
	Select,
	Space,
	Table,
	Typography,
} from "antd";
import { DeleteOutlined, EditOutlined, FilterOutlined, PlusOutlined } from "@ant-design/icons";

import { loadAllProject } from "../../../redux/rtk/features/projectManagement/project/project/project";
import "../Project.css";
import {
	addSingleProjectTeam,
	deleteProjectTeam,
	loadProjectRoles,
	loadAllProjectTeam,
	loadAllProjectTeamByProjectId,
	loadProjectTeamDetailsByProjectId,
} from "../../../redux/rtk/features/projectManagement/project/projectTeam/projectTeam";
import { loadAllStaff } from "../../../redux/rtk/features/user/userSlice";
import { loadProjectWBS } from "../../../redux/rtk/features/projectManagement/project/projectWBS/projectWBS";
import FilterPanel from "../../hiring/FilterPanel";
import "../../hiring/FilterPanel.css";

const DATE_FORMAT = "YYYY-MM-DD";

const numberOrDefault = (value, fallback = 0) => {
	const numeric = Number(value);
	return Number.isFinite(numeric) ? numeric : fallback;
};

const mapTeamRecord = (record, index) => {
	const projectId = Number(record?.ProjectId ?? record?.projectId ?? 0);
	const employeeId = Number(record?.EmployeeId ?? record?.employeeId ?? 0);
	const roleId = Number(record?.RoleId ?? record?.roleId ?? 0);
	const assignedWBSId = Number(record?.AssignedWBSId ?? record?.assignedWBSId ?? 0);
	const uniqueKey =
		record?.ProjectTeamId ??
		record?.projectTeamId ??
		`${projectId || "p"}-${employeeId || "e"}-${roleId || "r"}-${assignedWBSId || "w"}-${index}`;
	return {
		key: uniqueKey,
		ProjectTeamId: uniqueKey,
		ProjectId: projectId,
		ProjectName: record?.ProjectName ?? record?.projectName ?? "",
		EmployeeId: employeeId,
		EmployeeName: record?.EmployeeName ?? record?.employeeName ?? "",
		RoleId: roleId,
		RoleName: record?.RoleName ?? record?.roleName ?? "",
		AssignedWBSId: assignedWBSId,
		WBSName: record?.WBSName ?? record?.wbsName ?? "",
		IsLead: Boolean(record?.IsLead ?? record?.isLead),
		StartDate: record?.StartDate ?? record?.startDate ?? null,
		EndDate: record?.EndDate ?? record?.endDate ?? null,
		AllocationPercent:
			numberOrDefault(record?.AllocationPercent ?? record?.allocationPercent, 0),
		Remarks: record?.Remarks ?? record?.remarks ?? "",
		DataStatus: record?.DataStatus ?? record?.dataStatus,
	};
};

const ProjectTeamDetailsSetup = () => {
	const dispatch = useDispatch();
	const location = useLocation();
	const [form] = Form.useForm();
	const [editingId, setEditingId] = useState(null);
	const [teamMemberModalOpen, setTeamMemberModalOpen] = useState(false);
	const [filterOpen, setFilterOpen] = useState(false);
	const [filterDateRange, setFilterDateRange] = useState([null, null]);
	const [filterEmployee, setFilterEmployee] = useState("");
	const [filterRole, setFilterRole] = useState("");
	const [filterLead, setFilterLead] = useState("");
	const [selectedProjectId, setSelectedProjectId] = useState(() => {
		const projectIdFromState = location?.state?.projectId ?? null;
		return projectIdFromState ? Number(projectIdFromState) : null;
	});

	const { list: projectList = [], loading: projectLoading } = useSelector(
		(state) => state.project || {}
	);
	const { list: staffList = [], loading: staffLoading } = useSelector(
		(state) => state.users || {}
	);
	const { list: wbsList = [], loading: wbsLoading } = useSelector(
		(state) => state.projectWBS || {}
	);
	const {
		list: teamList = [],
		roles: roleList = [],
		rolesLoading,
		loading: teamLoading,
	} = useSelector((state) => state.projectTeam || {});

	useEffect(() => {
		dispatch(loadAllProject());
		dispatch(loadAllStaff({ status: "true" }));
		dispatch(loadProjectRoles());
		dispatch(loadAllProjectTeam());
	}, [dispatch]);

	useEffect(() => {
		if (selectedProjectId) {
			dispatch(loadProjectTeamDetailsByProjectId(selectedProjectId));
			dispatch(loadProjectWBS(selectedProjectId));
			form.setFieldsValue({ ProjectId: selectedProjectId });
		} else {
			dispatch(loadAllProjectTeam());
		}
	}, [dispatch, selectedProjectId, form]);

	const projectOptions = useMemo(
		() =>
			projectList.map((project) => ({
				value: Number(project.ProjectId ?? project.id),
				label: project.ProjectName ?? project.name ?? "Unknown",
			})),
		[projectList]
	);

	const employeeOptions = useMemo(
		() =>
			staffList.map((staff) => {
				const fallbackName = `${staff.firstName ?? ""} ${staff.lastName ?? ""}`.trim();
				const label = staff.EmployeeName ?? fallbackName;
				return {
					value: Number(staff.EmployeeId ?? staff.id),
					label: label || "Unknown",
				};
			}),
		[staffList]
	);

	const wbsOptions = useMemo(
		() =>
			wbsList
				.filter((item) => {
					const projectId = Number(item.ProjectId ?? item.projectId);
					return !selectedProjectId || projectId === Number(selectedProjectId);
				})
				.map((wbs) => ({
					value: Number(wbs.WBSId ?? wbs.wbsId),
					label: wbs.WBSName ?? wbs.wbsName ?? "Unknown",
				})),
		[selectedProjectId, wbsList]
	);

	const roleOptions = useMemo(
		() =>
			roleList.map((role) => ({
				value: Number(role.RoleId ?? role.roleId),
				label: role.RoleName ?? role.roleName ?? "Unknown",
			})),
		[roleList]
	);

	const filterEmployeeOptions = useMemo(
		() => [
			{ value: "", label: "Select Employee" },
			...(employeeOptions || []),
		],
		[employeeOptions]
	);

	const filterRoleOptions = useMemo(
		() => [
			{ value: "", label: "Select Role" },
			...(roleOptions || []),
		],
		[roleOptions]
	);

	const filterLeadOptions = useMemo(
		() => [
			{ value: "", label: "Select Lead" },
			{ value: "yes", label: "Yes" },
			{ value: "no", label: "No" },
		],
		[]
	);

	const mappedTeamList = useMemo(
		() => (Array.isArray(teamList) ? teamList.map(mapTeamRecord) : []),
		[teamList]
	);

	const filteredTeamList = useMemo(() => {
		let result = mappedTeamList;
		if (selectedProjectId) {
			result = result.filter(
				(record) => Number(record.ProjectId) === Number(selectedProjectId)
			);
		}
		if (filterEmployee) {
			result = result.filter(
				(record) => Number(record.EmployeeId) === Number(filterEmployee)
			);
		}
		if (filterRole) {
			result = result.filter(
				(record) => Number(record.RoleId) === Number(filterRole)
			);
		}
		if (filterLead === "yes") {
			result = result.filter((record) => Boolean(record.IsLead));
		} else if (filterLead === "no") {
			result = result.filter((record) => !record.IsLead);
		}
		if (filterDateRange && (filterDateRange[0] || filterDateRange[1])) {
			const startD = filterDateRange[0] ? dayjs(filterDateRange[0]) : null;
			const endD = filterDateRange[1] ? dayjs(filterDateRange[1]) : null;
			result = result.filter((record) => {
				const pStart = record?.StartDate ?? null;
				const pEnd = record?.EndDate ?? null;
				if (!pStart && !pEnd) return true;
				const projStart = pStart ? dayjs(pStart) : null;
				const projEnd = pEnd ? dayjs(pEnd) : null;
				if (startD && projEnd && projEnd.isBefore(startD)) return false;
				if (endD && projStart && projStart.isAfter(endD)) return false;
				return true;
			});
		}
		return result;
	}, [
		mappedTeamList,
		selectedProjectId,
		filterEmployee,
		filterRole,
		filterLead,
		filterDateRange,
	]);

	const handleClearFilters = useCallback(() => {
		setFilterDateRange([null, null]);
		setFilterEmployee("");
		setFilterRole("");
		setFilterLead("");
	}, []);

	const resetForm = useCallback(() => {
		setEditingId(null);
		form.resetFields();
		form.setFieldsValue({
			ProjectId: selectedProjectId ?? null,
			RoleId: null,
			IsLead: false,
			AllocationPercent: 0,
		});
	}, [form, selectedProjectId]);

	const handleProjectChange = useCallback(
		(value) => {
			setSelectedProjectId(value || null);
			form.setFieldsValue({ ProjectId: value || null, AssignedWBSId: null });
			if (value) {
				dispatch(loadAllProjectTeamByProjectId(value));
				dispatch(loadProjectWBS(value));
			}
		},
		[dispatch, form]
	);

	const handleSubmit = useCallback(
		async (values) => {
			const payload = {
				ProjectTeamId: editingId ?? 0,
				ProjectId: Number(values.ProjectId),
				EmployeeId: Number(values.EmployeeId),
				RoleId: Number(values.RoleId ?? 0),
				AssignedWBSId: Number(values.AssignedWBSId ?? 0),
				IsLead: Boolean(values.IsLead),
				StartDate: values.StartDate ? dayjs(values.StartDate).format(DATE_FORMAT) : null,
				EndDate: values.EndDate ? dayjs(values.EndDate).format(DATE_FORMAT) : null,
				AllocationPercent: Number(values.AllocationPercent ?? 0),
				Remarks: values.Remarks ?? "",
				DataStatus: 1,
			};

			const response = await dispatch(addSingleProjectTeam(payload));
			if (response?.meta?.requestStatus !== "rejected") {
				if (payload.ProjectId) {
					dispatch(loadProjectTeamDetailsByProjectId(payload.ProjectId));
					setSelectedProjectId(payload.ProjectId);
				} else {
					dispatch(loadAllProjectTeam());
				}
				resetForm();
				setTeamMemberModalOpen(false);
			}
		},
		[dispatch, editingId, resetForm]
	);

	const handleEdit = useCallback(
		(record) => {
			setEditingId(record.ProjectTeamId ?? record.projectTeamId ?? null);
			const startDateValue = record.StartDate ? dayjs(record.StartDate) : null;
			const endDateValue = record.EndDate ? dayjs(record.EndDate) : null;
			if (record.ProjectId) {
				const numericProjectId = Number(record.ProjectId);
				setSelectedProjectId(numericProjectId);
				dispatch(loadProjectWBS(numericProjectId));
			}
			form.setFieldsValue({
				ProjectId: Number(record.ProjectId ?? 0) || null,
				EmployeeId: Number(record.EmployeeId ?? 0) || null,
				RoleId: Number(record.RoleId ?? 0) || null,
				AssignedWBSId: Number(record.AssignedWBSId ?? 0) || null,
				IsLead: Boolean(record.IsLead),
				StartDate: startDateValue,
				EndDate: endDateValue,
				AllocationPercent: Number(record.AllocationPercent ?? 0),
				Remarks: record.Remarks ?? "",
			});
			setTeamMemberModalOpen(true);
		},
		[dispatch, form]
	);

	const handleDelete = useCallback(
		async (record) => {
			const response = await dispatch(deleteProjectTeam(record));
			if (response?.meta?.requestStatus !== "rejected" && selectedProjectId) {
				dispatch(loadProjectTeamDetailsByProjectId(selectedProjectId));
			} else if (response?.meta?.requestStatus !== "rejected") {
				dispatch(loadAllProjectTeam());
			}
		},
		[dispatch, selectedProjectId]
	);

	const columns = useMemo(
		() => [
			{
				title: "Project",
				dataIndex: "ProjectName",
				key: "ProjectName",
				width: 160,
				render: (_, record) => record.ProjectName || record.ProjectId,
			},
			{
				title: "Employee",
				dataIndex: "EmployeeName",
				key: "EmployeeName",
				width: 160,
				render: (_, record) => record.EmployeeName || record.EmployeeId,
			},
			{
				title: "Role",
				dataIndex: "RoleName",
				key: "RoleName",
				width: 120,
				render: (_, record) => record.RoleName || record.RoleId || "-",
			},
			{
				title: "WBS",
				dataIndex: "WBSName",
				key: "WBSName",
				width: 140,
				render: (_, record) => record.WBSName || record.AssignedWBSId || "-",
			},
			{
				title: "Lead",
				dataIndex: "IsLead",
				key: "IsLead",
				width: 70,
				render: (value) => (value ? "Yes" : "No"),
			},
			{
				title: "Start Date",
				dataIndex: "StartDate",
				key: "StartDate",
				width: 110,
				render: (value) => (value ? dayjs(value).format(DATE_FORMAT) : "-"),
			},
			{
				title: "End Date",
				dataIndex: "EndDate",
				key: "EndDate",
				width: 110,
				render: (value) => (value ? dayjs(value).format(DATE_FORMAT) : "-"),
			},
			{
				title: "%",
				dataIndex: "AllocationPercent",
				key: "AllocationPercent",
				width: 70,
			},
			{
				title: "Remarks",
				dataIndex: "Remarks",
				key: "Remarks",
				width: 120,
			},
			{
				title: "Actions",
				key: "actions",
				width: 110,
				render: (_, record) => (
					<Space>
						<Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
						<Popconfirm
							title="Delete team member"
							onConfirm={() => handleDelete(record)}
							okText="Yes"
							cancelText="No"
						>
							<Button type="link" danger icon={<DeleteOutlined />} />
						</Popconfirm>
					</Space>
				),
			},
		],
		[handleDelete, handleEdit]
	);

	return (
		<div className="project-team-page-container">
			<Row gutter={[16, 16]} className="project-team-row">
				<Col xs={24} className="project-team-table-col">
					<div className="flex justify-between items-center mb-4">
						<span />
						<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
							<button
								type="button"
								className="filter-panel-trigger"
								onClick={() => setFilterOpen(true)}
								aria-label="Filters"
							>
								<FilterOutlined style={{ fontSize: 16 }} />
							</button>
							<Button
								type="primary"
								className="project-apply-btn"
								onClick={() => { resetForm(); setTeamMemberModalOpen(true); }}
								icon={<PlusOutlined />}
							>
								Add Team Member
							</Button>
						</div>
					</div>
					{filterOpen &&
						createPortal(
							<div
								style={{
									position: "fixed",
									top: 80,
									right: 24,
									zIndex: 1050,
									minWidth: 280,
									maxWidth: 320,
								}}
							>
								<FilterPanel
									open={true}
									onClose={() => setFilterOpen(false)}
									dateRange={filterDateRange}
									onDateRangeChange={setFilterDateRange}
									filters={[
										{
											key: "employee",
											label: "Employee",
											placeholder: "Select Employee",
											value: filterEmployee,
											onChange: setFilterEmployee,
											options: filterEmployeeOptions,
											showSearch: true,
										},
										{
											key: "role",
											label: "Role",
											placeholder: "Select Role",
											value: filterRole,
											onChange: setFilterRole,
											options: filterRoleOptions,
											showSearch: true,
										},
										{
											key: "lead",
											label: "Lead",
											placeholder: "Select Lead",
											value: filterLead,
											onChange: setFilterLead,
											options: filterLeadOptions,
										},
									]}
									onClearAll={handleClearFilters}
								/>
							</div>,
							document.body
						)}
					<div className="ant-card p-4 rounded  project-team-table-container">
						<div className="flex my-2 justify-between">
							<h5 className="text-xl mb-2">Project Team</h5>
							<Select
								style={{ width: 200 }}
								placeholder="Filter by Project"
								showSearch
								allowClear
								value={selectedProjectId}
								onChange={handleProjectChange}
								options={projectOptions}
								loading={projectLoading}
								filterOption={(input, option) =>
									(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
								}
							/>
						</div>
						<Table
							dataSource={filteredTeamList}
							columns={columns}
							pagination={{
								defaultPageSize: 20,
								showSizeChanger: true,
								showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
								pageSizeOptions: ['10', '20', '50', '100'],
							}}
							scroll={{ x: true, y: "100%" }}
							loading={teamLoading}
						/>
					</div>
				</Col>
			</Row>
			<Modal
				title={editingId ? "Edit Team Member" : "Add Team Member"}
				open={teamMemberModalOpen}
				onCancel={() => { setTeamMemberModalOpen(false); resetForm(); }}
				footer={null}
				width={560}
				destroyOnClose
			>
				<Form form={form} layout="vertical" onFinish={handleSubmit}>
							<Form.Item
								label="Project"
								name="ProjectId"
								rules={[{ required: true, message: "Select project" }]}
							>
								<Select
									options={projectOptions}
									showSearch
									placeholder="Select project"
									loading={projectLoading}
									onChange={handleProjectChange}
									allowClear
									filterOption={(input, option) =>
										(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
									}
								/>
							</Form.Item>

							<Form.Item
								label="Employee"
								name="EmployeeId"
								rules={[{ required: true, message: "Select employee" }]}
							>
								<Select
									options={employeeOptions}
									showSearch
									placeholder="Select employee"
									loading={staffLoading}
									allowClear
									filterOption={(input, option) =>
										(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
									}
								/>
							</Form.Item>

							<Form.Item
								label="Role"
								name="RoleId"
								rules={[{ required: true, message: "Select role" }]}
							>
								<Select
									options={roleOptions}
									showSearch
									placeholder="Select role"
									loading={rolesLoading}
									allowClear
									filterOption={(input, option) =>
										(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
									}
								/>
							</Form.Item>

							<Form.Item label="Assigned WBS" name="AssignedWBSId">
								<Select
									options={wbsOptions}
									showSearch
									placeholder="Select WBS"
									loading={wbsLoading}
									allowClear
									filterOption={(input, option) =>
										(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
									}
								/>
							</Form.Item>

							<Form.Item name="IsLead" valuePropName="checked">
								<Checkbox>Is Lead</Checkbox>
							</Form.Item>

							<Form.Item label="Start Date" name="StartDate">
								<DatePicker style={{ width: "100%" }} format={DATE_FORMAT} />
							</Form.Item>

							<Form.Item label="End Date" name="EndDate">
								<DatePicker style={{ width: "100%" }} format={DATE_FORMAT} />
							</Form.Item>

							<Form.Item label="Allocation %" name="AllocationPercent">
								<InputNumber style={{ width: "100%" }} min={0} max={100} />
							</Form.Item>

							<Form.Item label="Remarks" name="Remarks">
								<Input.TextArea rows={3} placeholder="Remarks" />
							</Form.Item>

							<div style={{ textAlign: "right" }}>
								<Button type="primary" htmlType="submit" loading={teamLoading} disabled={teamLoading}>
									{editingId ? "Update" : "Add"}
								</Button>
							</div>
						</Form>
			</Modal>
		</div>
	);
};

export default ProjectTeamDetailsSetup;
